import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import JSON5 from 'json5';

import { Lookup } from '@shared/interfaces/lookup';
import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { SasLabelValueComponent } from '@shared/sas-label-value/sas-label-value.component';
import { FormSubmittedService } from '@shared/services/form-submitted.service';
import { LookupUseCacheService } from '@shared/services/lookup-use-cache.service';

@Component({
    selector: 'sas-select-field-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, TranslateModule, SasLabelValueComponent],
    providers: [],
    template: `
        @if (isShowingSummaryInfo) {
            <sas-label-value [label]="field.props.label ?? ''"
                             [model]="selectedValue"
                             sasClass="textmode toplabel mb-2">
            </sas-label-value>
        } @else {
            <div class="sas-select-wrapper mb-3">
                <label [id]="field.id + 'label'"
                        [for]="instanceId"
                        class="form-label"
                        [ngClass]="{'has-error' : field.formControl.invalid && (field.formControl.dirty || field.formControl.touched)}"
                        translate="{{field.props.label}}"
                        title="{{field.props.label ?? '' | translate}}"
                        [hidden]="field.props['hideLabel']">
                </label>
                @if (field.props.required) {
                <span> *</span>
                }
                <div [id]="field.id + 'Help'" [title]="helpText" class="sas-inline-block">
                    @if (helpText?.length && !isReadonly) {
                    <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;" aria-hidden="true"></i>
                    }
                </div>
                <select [name]="field.name"
                        [id]="instanceId"
                        class="form-select sas-select {{field.props['sasClass']}}"
                        [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || field.formControl.touched || formSubmittedService.isSubmitted())}"
                        title="{{helpText}}"
                        [formControl]="formControl"
                        (change)="field.props['sasOnSelect($event)']"
                        required="{{field.props.required}}"
                        tabindex="0"
                        [attr.aria-labelledby]="field.id + 'label'"
                        [attr.aria-disabled]="isReadonly"
                        [attr.aria-required]="field.props.required"
                        [attr.aria-describedby]="field.id + 'Help'">
                    <option [ngValue]="undefined" *ngIf="field.props['defaultText']">{{field.props['defaultText'] | translate}}</option>
                    @for (item of sasOptions; track $index ){
                    <option [ngValue]="item.id">{{item.name | translate}}</option>
                    }
                </select>
                @if (field.props['sasHelptext']) {
                <div [id]="instanceId + 'Desc'" title="{{field.props['sasHelptext'] ?? '' | translate}}" class="sas-inline-block">
                    <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                </div>
                }
                <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                    <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
                </div>
            </div>
        }
    `,
    styleUrl: './sas-select-field-type.component.scss'
})
/*@for (item of field.props['sasOptions']; track $index ) {*/
export class SasSelectFieldTypeComponent extends FieldType<FieldTypeConfig> {
    http = inject(HttpClient);
    formSubmittedService = inject(FormSubmittedService);
    instanceId: string = Math.random().toString(36).substring(2);
    sasOptions: Array<Lookup> = [];
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);
    private lookupUseCacheService = inject(LookupUseCacheService);

    helpText: string = '';
    isShowingSummaryInfo: boolean = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    get selectedValue(): string {
        const { value } = this.formControl;
        if (!value) return '';

        const selectedOption = this.sasOptions.find(option => option.id === value);
        return selectedOption ? selectedOption.name : '';
    }

    constructor() {
        super()
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.instanceId = this.field.id + Math.random().toString(36).substring(2);

        this.field.formControl.errors?.['required'];
        if (this.field.props['dataSource']?.type === 'api') {
            this.lookupUseCacheService.getOptions(this.field.props['dataSource'].url)
                .subscribe((result: Array<Lookup>) => {
                    this.sasOptions = result;
                    this.sasOptions = this.sortOptions(this.sasOptions);
                    this.field.formControl.setValue(this.field.model[`${this.field.key!}`]);
                });
        }
        else if (this.field.props['dataSource']?.type === 'static'
            && (this.field.props['dataSource']?.source !== '' && this.field.props['dataSource']?.source !== '[]') ) {
            const parsedOptions = JSON5.parse(this.field.props['dataSource'].source);
            const options = JSON.stringify(parsedOptions);  // Convert it back to a standard JSON string
            this.sasOptions = this.sortOptions(JSON.parse(options));
        }
        else {
            this.sasOptions = this.sortOptions(this.field.props['sasOptions']);
        }

        this.isShowingSummaryInfo = this.field.props['isShowingSummaryInfo'] ?? false;
    }

    ngDoCheck() {
        if (this.isReadonly && !this.formControl.disabled) {
            this.formControl.disable({ emitEvent: false });
        }

        if (!this.isReadonly && this.formControl.disabled) {
            this.formControl.enable({ emitEvent: false });
        }
    }

    onSelect(ev: Event): void {
        if (this.field.props['sasOnSelect'] !== undefined) {
            this.field.props['sasOnSelect'].emit({
                'value': this.model,
                'selectId': this.instanceId,
                'selectName': this.field.name
            });
        }
    }

    sortOptions(options: Array<Lookup>): Array<Lookup> {
        return options.toSorted((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }));
    }

    //select(ev: Event): void {
    //    const target = ev.target as HTMLElement;
    //    document.getElementById(this.instanceId)?.click()
    //}
}
